/**
 * Management tab (Spec 201 — entity ↔ plan relationship)
 *
 * The single place to see and manage which lakehouse/warehouse is protected by which plan.
 * One unified list of EVERY backup-capable entity with its current plan (or "Unprotected"):
 *  - assign unprotected entities to a plan,
 *  - reassign (move) already-protected entities to a different plan,
 *  - in bulk (select many → one plan) or per-row.
 * Assigning to a plan is a MOVE — an entity belongs to exactly one plan; its schedule and storage
 * come from that plan, and retention resolves source → plan → default (Spec 214 D6). (Granting the
 * backup SP workspace access is a separate admin step.)
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, Warehouse, ArrowLeftRight, ShieldCheck } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Button,
  Badge,
  Checkbox,
  Select,
  Modal,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { useAuth } from '../../hooks/useAuth';
import { assignmentApi, AssignableEntity, BackupEntityType } from '../../api/assignment';
import { plansApi } from '../../api/plans';
import { ListFilterBar, textMatch } from '../common/ListFilterBar';

const key = (e: { entityType: BackupEntityType; entityId: string }) => `${e.entityType}:${e.entityId}`;

const envVariant: Record<string, 'danger' | 'info' | 'warning' | 'default'> = {
  prod: 'danger',
  dev: 'info',
  uat: 'warning',
  test: 'default',
};

const planStatusVariant: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success',
  paused: 'warning',
  draft: 'info',
  archived: 'default',
};

type PlanRef = { planId: string; planName: string; planStatus: string };

export const PlanAssignment: React.FC = () => {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']);
  const qc = useQueryClient();

  const entities = useQuery({ queryKey: ['backup', 'assignable-entities'], queryFn: assignmentApi.assignableEntities });

  // Spec 214 T051/T074: the governing plan rides each entity row from ONE backend query
  // (UX_bpt_entity guarantees one plan per entity) — the old per-plan plansApi.get() fan-out
  // (N+1) is gone; the plans list is only needed for the pickers.
  const dataQ = useQuery({ queryKey: ['backup', 'plan-assignment-data'], queryFn: plansApi.list });
  const plans = dataQ.data ?? [];
  const planById = useMemo(() => new Map(plans.map((p) => [p.id, p])), [plans]);
  const activePlans = useMemo(() => plans.filter((p) => p.status !== 'archived'), [plans]);
  const planOf = (e: AssignableEntity): PlanRef | undefined =>
    e.planId ? { planId: e.planId, planName: e.planName ?? e.planId, planStatus: e.planStatus ?? 'active' } : undefined;

  // Filters: search + type + env + protection status + plan.
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [env, setEnv] = useState('');
  const [statusF, setStatusF] = useState(''); // '' | 'protected' | 'unprotected'
  const [planF, setPlanF] = useState(''); // plan id

  const rows = useMemo(() => {
    let list = (entities.data ?? []).slice();
    if (type) list = list.filter((e) => e.entityType === type);
    if (env) list = list.filter((e) => e.envHint === env);
    if (statusF) list = list.filter((e) => (statusF === 'protected' ? !!planOf(e) : !planOf(e)));
    if (planF) list = list.filter((e) => planOf(e)?.planId === planF);
    if (q) list = list.filter((e) => textMatch(q, e.name, e.entityType, e.workspaceName, e.envHint, planOf(e)?.planName));
    return list.sort(
      (a, b) => (a.workspaceName ?? '').localeCompare(b.workspaceName ?? '') || (a.name ?? '').localeCompare(b.name ?? '')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities.data, type, env, statusF, planF, q, dataQ.data]);

  // Selection (bulk assign/reassign).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (e: AssignableEntity) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(key(e)) ? n.delete(key(e)) : n.add(key(e));
      return n;
    });
  const allShown = rows.length > 0 && rows.every((e) => selected.has(key(e)));
  const toggleAll = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allShown) rows.forEach((e) => n.delete(key(e)));
      else rows.forEach((e) => n.add(key(e)));
      return n;
    });
  const chosen = (entities.data ?? []).filter((e) => selected.has(key(e)));

  const [bulkPlanId, setBulkPlanId] = useState('');
  const [busy, setBusy] = useState(false);
  const [changeFor, setChangeFor] = useState<AssignableEntity | null>(null);

  const [assignResult, setAssignResult] = useState<string | null>(null);

  /** Assign/move the entities to a plan: attach each (attach = move, one plan per entity), point
   *  their storage at the plan's destination, materialize schedules, then refresh everything.
   *  Spec 214 FR-029/FR-003: per-item failures are collected and SURFACED — the old sequential
   *  awaits threw unhandled (no user feedback) and stopped mid-batch. */
  const assignToPlan = async (list: AssignableEntity[], planId: string) => {
    if (list.length === 0 || !planId) return;
    setBusy(true);
    setAssignResult(null);
    const failures: string[] = [];
    let captureEnabled = false;
    try {
      const plan = planById.get(planId);
      for (const e of list) {
        try {
          const t = await plansApi.attachTarget(planId, e.entityType, e.entityId);
          if ((t as { captureEnabled?: boolean }).captureEnabled) captureEnabled = true;
        } catch (err) {
          failures.push(`${e.name ?? e.entityId}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
      const succeeded = list.filter((e) => !failures.some((f) => f.startsWith(`${e.name ?? e.entityId}:`)));
      if (plan?.storageAccountId && succeeded.length) {
        await assignmentApi.bulkAssign(
          succeeded.map((e) => ({ entityType: e.entityType, entityId: e.entityId })),
          plan.storageAccountId,
          true
        ).catch((err) => failures.push(`destination assignment: ${err instanceof Error ? err.message : String(err)}`));
      }
      await assignmentApi.ensureSchedules().catch(() => undefined);
      setAssignResult(
        failures.length > 0
          ? `${succeeded.length} assigned · ${failures.length} failed — ${failures[0]}${failures.length > 1 ? ` (+${failures.length - 1} more)` : ''}`
          : `${succeeded.length} assigned to ${plan?.name ?? 'plan'}${captureEnabled ? ' · definition capture auto-enabled' : ''}`
      );
      if (failures.length === 0) {
        setSelected(new Set());
        setBulkPlanId('');
        setChangeFor(null);
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['backup', 'assignable-entities'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'plan-assignment-data'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'plans'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'schedule-spread'] }),
      ]);
    } finally {
      setBusy(false);
    }
  };

  if (entities.isLoading || dataQ.isLoading) return <LoadingState rows={5} />;
  if (entities.isError) return <ErrorState onRetry={() => entities.refetch()} />;
  // Spec 214 FR-036: a failed plans fetch is an ERROR, not an empty catalog — the old
  // unchecked dataQ.isError fell through to the misleading "No backup plans yet" state.
  if (dataQ.isError) return <ErrorState onRetry={() => dataQ.refetch()} />;

  const protectedCount = (entities.data ?? []).filter((e) => !!planOf(e)).length;
  const total = entities.data?.length ?? 0;
  const resetFilters = () => {
    setQ('');
    setType('');
    setEnv('');
    setStatusF('');
    setPlanF('');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Entity → plan assignments"
          subtitle={`${protectedCount} of ${total} protected — assign unprotected entities, or move entities between plans`}
        />
        <CardBody>
          {assignResult && (
            <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
              {assignResult}
            </div>
          )}
          {plans.length === 0 ? (
            <EmptyState
              title="No backup plans yet"
              message="Create a plan in the Plans tab first — a plan bundles one schedule + one storage + one retention. Then assign entities to it here."
            />
          ) : (
            <>
              <ListFilterBar query={q} onQuery={setQ} placeholder="Search entities or plans…" count={rows.length} total={total}>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  options={[
                    { value: '', label: 'All types' },
                    { value: 'lakehouse', label: 'Lakehouse' },
                    { value: 'warehouse', label: 'Warehouse' },
                  ]}
                />
                <Select
                  value={env}
                  onChange={(e) => setEnv(e.target.value)}
                  options={[
                    { value: '', label: 'All envs' },
                    { value: 'prod', label: 'prod' },
                    { value: 'uat', label: 'uat' },
                    { value: 'dev', label: 'dev' },
                    { value: 'test', label: 'test' },
                  ]}
                />
                <Select
                  value={statusF}
                  onChange={(e) => setStatusF(e.target.value)}
                  options={[
                    { value: '', label: 'All entities' },
                    { value: 'protected', label: 'Protected' },
                    { value: 'unprotected', label: 'Unprotected' },
                  ]}
                />
                <Select
                  value={planF}
                  onChange={(e) => setPlanF(e.target.value)}
                  options={[{ value: '', label: 'All plans' }, ...plans.map((p) => ({ value: p.id, label: p.name }))]}
                />
                <Button variant="ghost" size="sm" onClick={resetFilters} disabled={!(q || type || env || statusF || planF)}>
                  Reset
                </Button>
              </ListFilterBar>

              {/* Bulk assign/reassign bar — appears when rows are selected. */}
              {canManage && selected.size > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-accent)] bg-[var(--color-bg-subtle)] px-3 py-2">
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">{selected.size} selected</span>
                  <span className="grow" />
                  <Select
                    value={bulkPlanId}
                    onChange={(e) => setBulkPlanId(e.target.value)}
                    options={[
                      { value: '', label: 'Choose a plan…' },
                      ...activePlans.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                  <Button variant="primary" size="sm" disabled={busy || !bulkPlanId} onClick={() => assignToPlan(chosen, bulkPlanId)}>
                    {busy ? 'Working…' : `Assign ${selected.size} to plan`}
                  </Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                </div>
              )}

              <DataTable<AssignableEntity>
                minWidth="58rem"
                columns={[
                  {
                    key: 'select',
                    header: canManage ? <Checkbox checked={allShown} onChange={toggleAll} aria-label="Select all" /> : '',
                    width: '2.5rem',
                    sortable: false,
                    render: (e) =>
                      canManage ? (
                        <span onClick={(ev) => ev.stopPropagation()}>
                          <Checkbox checked={selected.has(key(e))} onChange={() => toggle(e)} aria-label={`Select ${e.name}`} />
                        </span>
                      ) : null,
                  },
                  {
                    key: 'name',
                    header: 'Entity',
                    width: '20rem',
                    render: (e) => {
                      const Icon = e.entityType === 'warehouse' ? Warehouse : Database;
                      return (
                        <span className="flex min-w-0 items-start gap-2">
                          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
                          <span className="block min-w-0">
                            <span className="block font-medium text-[var(--color-text-primary)]">{e.name ?? e.entityId}</span>
                            <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
                              <Badge variant="default">{e.entityType}</Badge>
                              <span className="truncate">{e.workspaceName ?? '—'}</span>
                              {e.envHint && <Badge variant={envVariant[e.envHint] ?? 'default'}>{e.envHint}</Badge>}
                            </span>
                          </span>
                        </span>
                      );
                    },
                  },
                  {
                    key: 'plan',
                    header: 'Plan',
                    sortable: false,
                    render: (e) => {
                      const p = planOf(e);
                      return p ? (
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{p.planName}</span>
                          <Badge variant={planStatusVariant[p.planStatus] ?? 'default'}>{p.planStatus}</Badge>
                        </span>
                      ) : (
                        <Badge variant="warning">Unprotected</Badge>
                      );
                    },
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    width: '12rem',
                    sortable: false,
                    render: (e) =>
                      canManage ? (
                        <Button variant="outline" size="sm" disabled={busy} onClick={() => setChangeFor(e)}>
                          {planOf(e) ? (
                            <>
                              <ArrowLeftRight className="h-4 w-4" /> Change plan
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="h-4 w-4" /> Assign plan
                            </>
                          )}
                        </Button>
                      ) : (
                        <span>—</span>
                      ),
                  },
                ]}
                rows={rows}
                rowKey={(e) => key(e)}
              />
            </>
          )}
        </CardBody>
      </Card>

      {/* Assign / change-plan modal — pick the destination plan. */}
      <Modal
        isOpen={!!changeFor}
        onClose={() => setChangeFor(null)}
        title={`${changeFor && planOf(changeFor) ? 'Change plan' : 'Assign plan'} — ${changeFor?.name ?? changeFor?.entityId ?? ''}`}
        size="lg"
      >
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          The entity will be protected by the chosen plan — its <strong>schedule and destination</strong> come
          from that plan; retention resolves <strong>source policy → plan policy → type default</strong>.
          An entity belongs to exactly one plan, so this moves it (any previous plan is detached) and its
          backup cadence switches to the new plan&apos;s immediately.
        </p>
        {activePlans.length === 0 ? (
          <p className="px-1 py-2 text-sm text-[var(--color-text-secondary)]">No plans available — create one in the Plans tab first.</p>
        ) : (
          <div className="max-h-80 space-y-1 overflow-auto">
            {activePlans.map((p) => {
              const current = changeFor && planOf(changeFor)?.planId === p.id;
              return (
                <button
                  key={p.id}
                  disabled={busy || !!current}
                  onClick={() => changeFor && assignToPlan([changeFor], p.id)}
                  className="flex w-full flex-col gap-0.5 rounded border border-[var(--color-border)] px-3 py-2 text-left hover:bg-[var(--color-bg-subtle)] disabled:opacity-60"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">{p.name}</span>
                    <Badge variant={planStatusVariant[p.status] ?? 'default'}>{p.status}</Badge>
                    {current && <Badge variant="info">current</Badge>}
                  </span>
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {p.storageAccountId ? 'Has a destination' : 'No destination set'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PlanAssignment;
